import type { LegalDoc } from "./types";

// Biometric data retention policy, served at /biometric-data-retention (linked from the 20 October
// 2026 announcement and the pricing-changes page). English master; translations would live in
// biometric.<lang>.ts and be registered in ./index.ts (English is the fallback).
//
// Wording rules: say "the administrator" / "the gallery's administrator" (never photographer or
// organiser), and never claim the law requires 12 months — 12 months is Photohawk's own limit, set
// under the "no longer than necessary" principle and inside every deadline listed in §8.
//
// Keep §3 in sync with the retention job (photohawk-serverless faceRetention.js) and with the
// privacy policy's §4 / §8 (gdpr.<lang>.ts).
//
// 1.1 (31 October 2026, owner): the administrator chooses how long face data is kept (No expiry by default,
// owner decision 29 Oct); archiving a gallery doesn't delete face data ("Archived doesn't do anything except
// move them to a different area and not visible in FTP"); switching face search off still deletes it after
// 30 days; a closed workspace's face data goes within 24 hours.
export const BIOMETRIC_POLICY_PATH = "/biometric-data-retention";

const biometricEn: LegalDoc = {
  title: "Biometric data retention policy",
  effectiveDate: "1 November 2026",
  version: "1.1",
  intro:
    "Face search is optional. This policy explains what face data Photohawk keeps when a gallery uses face (selfie) search, how long we keep it, and how and when we permanently delete it. It applies worldwide, to every gallery on Photohawk.",
  sections: [
    {
      heading: "1. What this policy covers",
      blocks: [
        {
          type: "p",
          text: "“Face data” means the biometric data created so that a person can find their own photos with a selfie:",
        },
        {
          type: "ul",
          items: [
            "Face templates: numerical representations of the faces detected in a gallery's photos, held in the gallery's face-search index (Amazon Rekognition, EU region) or, for galleries on one of our own face models, as face vectors.",
            "The face archive: small face crops saved privately so a gallery's face search can be rebuilt without re-reading the original photos. The archive is never shown to anyone and is never served publicly.",
          ],
        },
        {
          type: "p",
          text: "Face data is not a photo. Photos, orders, sales, downloads and bib/number search are not covered by this policy and are not affected by it: deleting face data never deletes a photo.",
        },
      ],
    },
    {
      heading: "2. Selfies used to search",
      blocks: [
        {
          type: "p",
          text: "A selfie uploaded to search a gallery is used only for that search: it is compared with the gallery's face templates and then discarded. Selfies are not stored, are not added to any face-search index and are not used to create a face template.",
        },
      ],
    },
    {
      heading: "3. When we delete face data",
      blocks: [
        {
          type: "p",
          text: "We permanently delete face data at the earliest of:",
        },
        {
          type: "ul",
          items: [
            "The period the gallery's administrator chose: 30, 90 or 180 days, or 12 months, after each photo was uploaded. This applies photo by photo, even if the gallery stays online. The default is No expiry: face data is not deleted by age, and is kept until one of the other events below.",
            "When the gallery is deleted, or when the photo is deleted.",
            "30 days after face search is switched off for the gallery. If the administrator switches face search back on within those 30 days, the deletion is cancelled.",
            "Within 24 hours of the administrator's workspace being closed.",
            "30 days after a person asks us to delete their face data or withdraws their consent (see §5).",
            "45 days after our yearly review finds that the face data is no longer necessary (see §7).",
          ],
        },
        {
          type: "p",
          text: "When all of a gallery's face data has been deleted, selfie search ends for that gallery. Its photos stay available to browse, buy and download, and bib/number search keeps working.",
        },
        {
          type: "p",
          text: "Archiving a gallery does not delete its face data: an archived gallery stays online and keeps its face search.",
        },
      ],
    },
    {
      heading: "4. How we delete it",
      blocks: [
        {
          type: "ul",
          items: [
            "An automated job runs every day. It deletes expired face templates from the gallery's face-search index and blacks out (or deletes) the matching crops in the face archive.",
            "Each photo whose face data has been deleted is marked so that it is never indexed for face search again — including when a gallery's face search is rebuilt.",
            "When a gallery is deleted, or its face search has been switched off for 30 days, its whole face-search index and face archive are deleted.",
            "Deletion is permanent: deleted face templates cannot be restored.",
          ],
        },
      ],
    },
    {
      heading: "5. Asking us to delete your face data",
      blocks: [
        {
          type: "p",
          text: "Anyone can ask for their face data to be deleted, or withdraw their consent to face search, at any time:",
        },
        {
          type: "ul",
          items: [
            "In the gallery: open selfie search and choose “Make a request”. Your request goes to the gallery's administrator and to Photohawk.",
            "By email: write to support@photohawk.com and tell us which gallery or event it concerns.",
          ],
        },
        {
          type: "p",
          text: "We match the faces in the gallery (or across the administrator's whole workspace) to the person, delete the matching face data and stop those photos from being indexed for face search again. We do this within 30 days of the request. To do it we do not keep a template of the person's face: we record only which photos and faces were removed. If photos of the same person are uploaded later, a new request covers them.",
        },
      ],
    },
    {
      heading: "6. Backups",
      blocks: [
        {
          type: "ul",
          items: [
            "Face templates in the face-search index (Amazon Rekognition) are not backed up: once deleted they are gone.",
            "The face archive and our own face vectors are kept in a private storage bucket without versioning or replication, so a deletion there is immediate.",
            "Our database backups (point-in-time recovery, kept for up to 35 days) hold references to faces (identifiers and positions in a photo), never face templates. They are purged automatically within 35 days.",
          ],
        },
      ],
    },
    {
      heading: "7. Yearly review",
      blocks: [
        {
          type: "p",
          text: "At least once a year we review whether the face data we hold is still necessary for face search. Any face data the review finds unnecessary is deleted within 45 days of that finding.",
        },
      ],
    },
    {
      heading: "8. Why these limits (legal basis)",
      blocks: [
        {
          type: "p",
          text: "Data-protection law asks that personal data be kept no longer than necessary for its purpose (the storage-limitation principle of the GDPR and UK GDPR, Article 5(1)(e)). The gallery's administrator decides how face search is used in their galleries, so they choose how long its face data is kept: a period of 30, 90 or 180 days or 12 months from upload, or No expiry. Whatever they choose, face data is deleted when the gallery is deleted, 30 days after face search is switched off and when their workspace is closed, and on request, and our yearly review (§7) checks that the face data we hold is still necessary. No law prescribes a single period.",
        },
        { type: "p", text: "Administrators whose galleries are subject to US state biometric-privacy laws should choose a period that meets their deadlines, including:" },
        {
          type: "ul",
          items: [
            "Illinois (Biometric Information Privacy Act): destroy biometric data when the purpose for collecting it has been satisfied, or within 3 years of the person's last interaction, whichever comes first — under a public written retention schedule such as this one.",
            "Texas (Capture or Use of Biometric Identifier Act): destroy within a reasonable time, and no later than one year after the purpose for collecting it ends.",
            "Colorado (Colorado Privacy Act, biometric amendments): delete when the purpose has been satisfied, within 24 months of the person's last interaction, or within 45 days of finding at a yearly review that it is no longer necessary — whichever comes first.",
            "Washington (RCW 19.375): keep biometric identifiers no longer than reasonably necessary to provide the service.",
          ],
        },
        {
          type: "p",
          text: "Face search runs only with the consent of the person searching, which can be withdrawn at any time (see §5). For more on how we handle personal data, see our privacy policy at photohawk.com/privacy.",
        },
      ],
    },
    {
      heading: "9. Contact",
      blocks: [
        {
          type: "p",
          text: "Questions about this policy, or a request about your face data: support@photohawk.com. CLOUDCONCUR LTD (company no. 13399913), trading as Photohawk, 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, United Kingdom.",
        },
      ],
    },
  ],
};

export default biometricEn;
